
/**
 * Formats an ISO 8601 duration string into a human-readable format
 * Example: "PT1H30M" -> "1 hr 30 min"
 * @param duration ISO 8601 duration string or regular text
 * @param showNAForEmpty Whether to show "N/A" for null/empty/zero values
 * @returns Human-readable duration string
 */
export function formatDuration(duration: string | null | undefined, showNAForEmpty: boolean = false): string {
  if (!duration || duration.trim() === '' || duration === '0') {
    return showNAForEmpty ? 'N/A' : '';
  }
  
  // Check for text-based zero durations (e.g., "0 minutes", "0 mins", "0 min", "0 hours", "0 hrs", "0 hr")
  const textZeroPattern = /^0\s*(minutes?|mins?|hours?|hrs?|hr)$/i;
  if (textZeroPattern.test(duration.trim())) {
    return showNAForEmpty ? 'N/A' : '';
  }
  
  // Check if it's an ISO 8601 duration format
  if (duration.startsWith('P')) {
    try {
      // Extract hours
      const hoursMatch = duration.match(/(\d+)H/);
      const hours = hoursMatch ? parseInt(hoursMatch[1]) : 0;
      
      // Extract minutes
      const minutesMatch = duration.match(/(\d+)M/);
      const minutes = minutesMatch ? parseInt(minutesMatch[1]) : 0;
      
      // If both are 0, treat as empty (handles cases like PT0M, PT0H, PT0H0M)
      if (hours === 0 && minutes === 0) {
        return showNAForEmpty ? 'N/A' : '';
      }
      
      // Format the output
      if (hours > 0 && minutes > 0) {
        return `${hours} hr ${minutes} min`;
      } else if (hours > 0) {
        return `${hours} hr`;
      } else if (minutes > 0) {
        return `${minutes} min`;
      }
    } catch (e) {
      console.error("Error parsing ISO duration:", e);
      return duration; // Return original string if parsing fails
    }
  }
  
  // If it's not in ISO format or parsing failed, return the original string
  return duration;
}

/**
 * Checks if the current time is after 6pm (18:00)
 * @returns boolean indicating if editing should be disabled due to time
 */
export function isAfter6PM(): boolean {
  const now = new Date();
  return now.getHours() >= 18;
}

/**
 * Gets the Monday and Sunday dates of the last completed week
 * @returns Object with start (Monday) and end (Sunday) dates
 */
export function getLastCompleteWeek(): { start: Date; end: Date } {
  const today = new Date();
  const currentDayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  
  // Calculate days to subtract to get to last Monday
  const daysToLastMonday = currentDayOfWeek === 0 ? 8 : currentDayOfWeek + 6; // If Sunday, go back 8 days; otherwise, go back (currentDay + 6) days
  
  const lastMonday = new Date(today);
  lastMonday.setDate(today.getDate() - daysToLastMonday);
  lastMonday.setHours(0, 0, 0, 0);
  
  const lastSunday = new Date(lastMonday);
  lastSunday.setDate(lastMonday.getDate() + 6);
  lastSunday.setHours(23, 59, 59, 999);
  
  return { start: lastMonday, end: lastSunday };
}

/**
 * Gets the Monday and Sunday bounds for any given date's week
 * @param date The date to get week bounds for
 * @returns Object with start (Monday) and end (Sunday) dates
 */
export function getWeekBounds(date: Date): { start: Date; end: Date } {
  const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  
  // Calculate days to subtract to get to Monday
  const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  
  const monday = new Date(date);
  monday.setDate(date.getDate() - daysToMonday);
  monday.setHours(0, 0, 0, 0);
  
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);
  
  return { start: monday, end: sunday };
}

/**
 * Formats a week range for display (with year)
 * @param start The start date (Monday)
 * @param end The end date (Sunday)
 * @returns Formatted string like "Dec 4 - Dec 10, 2023"
 */
export function formatWeekRange(start: Date, end: Date): string {
  const startMonth = start.toLocaleDateString('en-US', { month: 'short' });
  const startDay = start.getDate();
  const endMonth = end.toLocaleDateString('en-US', { month: 'short' });
  const endDay = end.getDate();
  const year = end.getFullYear();
  
  if (start.getMonth() === end.getMonth()) {
    return `${startMonth} ${startDay} - ${endDay}, ${year}`;
  } else {
    return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${year}`;
  }
}

/**
 * Formats a week range for subtitle display (without year)
 * @param start The start date (Monday)
 * @param end The end date (Sunday)
 * @returns Formatted string like "June 2 - 8"
 */
export function formatWeekRangeSubtitle(start: Date, end: Date): string {
  const startMonth = start.toLocaleDateString('en-US', { month: 'long' });
  const startDay = start.getDate();
  const endMonth = end.toLocaleDateString('en-US', { month: 'long' });
  const endDay = end.getDate();
  
  if (start.getMonth() === end.getMonth()) {
    return `${startMonth} ${startDay} - ${endDay}`;
  } else {
    return `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
  }
}

/**
 * Formats a week range with day names for detailed display
 * @param start The start date (Monday)
 * @param end The end date (Sunday)
 * @returns Formatted string like "Monday June 2 to Sunday June 8"
 */
export function formatWeekRangeWithDays(start: Date, end: Date): string {
  const startDay = start.toLocaleDateString('en-US', { weekday: 'long' });
  const startMonth = start.toLocaleDateString('en-US', { month: 'long' });
  const startDate = start.getDate();
  
  const endDay = end.toLocaleDateString('en-US', { weekday: 'long' });
  const endMonth = end.toLocaleDateString('en-US', { month: 'long' });
  const endDate = end.getDate();
  
  return `${startDay} ${startMonth} ${startDate} to ${endDay} ${endMonth} ${endDate}`;
}

/**
 * Gets the date range for the last 30 days
 * @returns Object with start and end dates
 */
export function getLast30Days(): { start: Date; end: Date } {
  const today = new Date();
  const end = new Date(today);
  end.setHours(23, 59, 59, 999);
  
  const start = new Date(today);
  start.setDate(today.getDate() - 29); // 30 days including today
  start.setHours(0, 0, 0, 0);
  
  return { start, end };
}
